import { useCallback, useEffect, useState } from 'react';
import { COOKIE_MODE, ruleSetsApi, type ApiSettings } from '../api';
import { DEMO } from '../demo';
import type { RuleSetList } from '../ruleSets';

/**
 * 2c rule sets — the registry as every screen sees it: the Rule sets screen, the Rule
 * Management set picker and the `set_fired` measure's set choice.
 *
 * One copy for the whole console: a registry write answers with the new list, `publish` hands
 * it to every mounted user, so a set created on one screen is in the other's picker at once.
 * `null` means NOT KNOWN (a load in flight, or one that failed) — never "there are none".
 * Password mode knows exactly one set (the decision API's) and never offers another.
 */

let current: RuleSetList | null = null;
const listeners = new Set<(list: RuleSetList | null) => void>();

/** Every mounted `useRuleSets` gets `list` (a registry write's answer, or a fresh read). */
export function publishRuleSets(list: RuleSetList | null): void {
  current = list;
  for (const l of listeners) l(list);
}

/** Tests: forget the shared copy. */
export function resetRuleSetsCache(): void {
  current = null;
}

export interface RuleSetsState {
  list: RuleSetList | null;
  error: string | null;
  reload: () => Promise<RuleSetList | null>;
}

/** `fetch: false` — follow the shared copy only (the rule editor: the screen under it has loaded it). */
export function useRuleSets(settings: ApiSettings, { fetch = true }: { fetch?: boolean } = {}): RuleSetsState {
  const [list, setList] = useState<RuleSetList | null>(current);
  const [error, setError] = useState<string | null>(null);
  // Keyed on the address, not the object (see useMasterLists).
  const { baseUrl } = settings;

  const reload = useCallback(async () => {
    try {
      const got = await ruleSetsApi.list({ baseUrl });
      setError(null);
      publishRuleSets(got);
      return got;
    } catch (e) {
      setError((e as Error).message);
      return null;
    }
  }, [baseUrl]);

  useEffect(() => {
    listeners.add(setList);
    // Password mode has no registry to ask (the one set is the shell's document): not known.
    if ((COOKIE_MODE || DEMO) && (fetch || current === null)) void reload();
    return () => {
      listeners.delete(setList);
    };
  }, [reload, fetch]);

  return { list: COOKIE_MODE || DEMO ? list : null, error, reload };
}
